import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { isNavigating } from './lib/nav-progress';

declare const window: any;

// Vue Router 4 has no native popstate signal in afterEach (vuejs/vue-router#2796).
// Record the timestamp of the most recent popstate event so afterEach can
// detect browser back/forward / iOS swipe / Android back / Cmd+[ navigations
// and force a `page-back` transition regardless of depth math.
let popstateAt = 0;
if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => { popstateAt = Date.now(); });
}

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        component: () => import('./pages/JobsPage.vue'),
        alias: ['/jobs', '/Home/Jobs'],
    },
    {
        path: '/listing/:id',
        component: () => import('./pages/JobDetailPage.vue'),
        alias: '/Home/JobDetails/:id',
    },
    {
        path: '/apply/:listingId',
        component: () => import('./pages/ApplyPage.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/postjob',
        component: () => import('./pages/PostJobPage.vue'),
        alias: '/Home/PostJob',
    },
    {
        path: '/myjobs',
        component: () => import('./layouts/MyJobsLayout.vue'),
        children: [
            {
                path: '',
                redirect: () => ({
                    name: window.__AUTH_STATUS__?.isAuthenticated
                        ? 'my-jobs-applications'
                        : 'my-jobs-saved'
                })
            },
            { path: 'saved', name: 'my-jobs-saved', component: () => import('./components/SavedJobsList.vue') },
            { path: 'applications', name: 'my-jobs-applications', component: () => import('./components/MyApplicationsList.vue') },
            { path: 'applications/preview', name: 'my-jobs-applications-preview', component: () => import('./components/myjobs/ApplicationCardPreview.vue') },
            // Legacy doorbell links. Kept as redirects, and kept HERE rather than
            // beside the workspace below, because an application id must never
            // quietly open the same-numbered conversation.
            { path: 'messages/:appId', redirect: { name: 'my-jobs-messages' } },
            { path: 'pool-messages/:threadId', redirect: { name: 'my-jobs-messages' } },
        ],
    },

    // Candidate messages is a full-width two-pane workspace, so it sits outside
    // MyJobsLayout: that shell is 768px with a tab strip and cannot host two
    // panes. Both routes render the same component; the conversation id keeps its
    // own path segment so it can never collide with the legacy
    // /myjobs/messages/{applicationId} links redirected above.
    {
        path: '/myjobs/messages',
        name: 'my-jobs-messages',
        component: () => import('./components/myjobs/CandidateMessages.vue'),
    },
    {
        path: '/myjobs/conversations/:conversationId',
        name: 'my-jobs-conversation',
        component: () => import('./components/myjobs/CandidateMessages.vue'),
    },
    { path: '/saved', redirect: { name: 'my-jobs-saved' } },
    { path: '/applications', redirect: { name: 'my-jobs-applications' } },
    {
        path: '/company/:slug',
        name: 'company-public',
        component: () => import('./pages/CompanyPublicPage.vue'),
    },
    // Catch-all for unmatched MVC routes (e.g. /Home/Error, /Home/Privacy)
    // Redirects to jobs page so users never see a blank screen.
    { path: '/Home/:pathMatch(.*)*', redirect: '/' },

    // SPA-level 404 — any path not matched above renders a branded not-found
    // page instead of a blank <router-view>. Server returns 200 because the
    // catch-all controller route delegates to the SPA shell.
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./pages/NotFoundPage.vue') },

    // Employer SPA — nested routes with EmployerLayout providing auth + inject dependencies.
    // The bare /employer path renders the public EmployerLandingPage (no auth required);
    // EmployerLayout skips its auth flow and chrome for that route. Registered employers
    // are forwarded to /employer/dashboard by the beforeEach guard below.
    {
        path: '/employer',
        component: () => import('./layouts/EmployerLayout.vue'),
        children: [
            { path: '', name: 'employer-landing', component: () => import('./pages/EmployerLandingPage.vue') },
            { path: 'onboarding', name: 'employer-onboarding', component: () => import('./pages/EmployerOnboardingPage.vue') },
            { path: 'dashboard', name: 'employer-dashboard', component: () => import('./components/employer/EmployerDashboard.vue') },
            { path: 'inbox', name: 'employer-inbox', component: () => import('./components/employer/ApplicationInbox.vue'), meta: { requiresApproval: true } },
            // Optional :conversationId so doorbell emails can deep-link a thread.
            { path: 'messages/:conversationId?', name: 'employer-messages', component: () => import('./components/employer/EmployerMessages.vue'), meta: { requiresApproval: true } },
            { path: 'candidates', name: 'employer-candidates', component: () => import('./components/employer/CandidateSearch.vue'), meta: { requiresApproval: true } },
            { path: 'profile', name: 'employer-profile', component: () => import('./components/employer/EmployerProfile.vue') },
            { path: 'listing/:id/edit', name: 'employer-edit-listing', component: () => import('./components/employer/EditListing.vue'), meta: { requiresApproval: true } },
            { path: 'settings/team', name: 'employer-team', component: () => import('./components/employer/TeamSettings.vue') },
            // Deliberately NO meta.requiresApproval: a suspended employer must still be able
            // to see and cancel their subscription, or they can be billed with no way to stop it.
            { path: 'settings/billing', name: 'employer-billing', component: () => import('./components/employer/BillingSettings.vue') },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(window.__APP_BASE__ || '/'),
    routes,
    // Defer scroll-to-top so it fires AFTER the leave transition completes
    // (160ms leave + 20ms safety buffer). Synchronous scroll-to-top during
    // a transform animation produces a visible jump. Saved positions still
    // apply immediately — they need to land before the new page mounts to
    // feel like a real history restore.
    // Reference: https://router.vuejs.org/guide/advanced/scroll-behavior.html#delaying-the-scroll
    scrollBehavior(_to, _from, savedPosition) {
        if (savedPosition) return savedPosition;
        return new Promise(resolve => setTimeout(() => resolve({ top: 0 }), 180));
    },
});

// Auth guards
router.beforeEach((to) => {
    // Spans the whole navigation, including the target route's dynamic
    // import, see nav-progress.ts for why that gap needs a visible signal.
    isNavigating.value = true;

    const auth = window.__AUTH_STATUS__;

    // Registered employers on the employer landing → route through
    // /employer/enter so the server can match their Scholaro identity to an
    // Employers row and issue the EmployerSession cookie. If we jumped straight
    // to /dashboard the cookie would never get minted and the layout would
    // bounce them right back to /Login. /employer/enter is an MVC action (not
    // a Vue route), so we use window.location for a real navigation.
    if (to.name === 'employer-landing' && auth?.isEmployer && !auth?.hasEmployerSession) {
        const base = (window.__APP_BASE__ || '/').replace(/\/+$/, '');
        window.location.href = base + '/employer/enter';
        return false;
    }
    // Already fully signed in (both cookies present) — dashboard shortcut.
    if (to.name === 'employer-landing' && auth?.hasEmployerSession) {
        return { name: 'employer-dashboard' };
    }

    // /postjob: after the Scholaro-auth pivot, /postjob requires BOTH a
    // Scholaro session AND an EmployerSession (the post endpoint rejects
    // anything else). Guests get sent through /Login → /employer/enter,
    // which decides: onboarded → dashboard → click Post; not onboarded →
    // onboarding form first.
    if ((to.path === '/postjob' || to.path === '/Home/PostJob')
        && !auth?.isAuthenticated
        && !auth?.hasEmployerSession) {
        const base = (window.__APP_BASE__ || '/').replace(/\/+$/, '');
        const returnUrl = encodeURIComponent(base + '/postjob');
        window.location.href = '/Login?ReturnUrl=' + returnUrl;
        return false;
    }

    // Authed Scholaro user without an EmployerSession cookie (fresh account
    // that has never completed employer onboarding). Send them through
    // /employer/enter — the MVC action resolves to dashboard or onboarding
    // based on whether an Employers row exists for their email, then honors
    // returnUrl to bring them back to /postjob with a minted EmployerSession
    // cookie. Without this guard, the page rendered the form the user
    // couldn't submit and the server rejection caused a redirect loop.
    if ((to.path === '/postjob' || to.path === '/Home/PostJob')
        && auth?.isAuthenticated
        && !auth?.hasEmployerSession) {
        const base = (window.__APP_BASE__ || '/').replace(/\/+$/, '');
        const returnUrl = base + '/postjob';
        window.location.href = base + '/employer/enter?returnUrl='
            + encodeURIComponent(returnUrl);
        return false;
    }

    // /employer/* sub-routes: auth is handled by EmployerLayout via /api/employer/session
    // (EmployerSession cookie is separate from the Scholaro user cookie — this guard
    // cannot see it, so we delegate the check to the layout).

    // Routes with requiresAuth meta: redirect to external login
    if (to.meta.requiresAuth && !auth?.isAuthenticated) {
        // __APP_BASE__ is Razor-emitted with a trailing slash (see _Layout.cshtml).
        // Strip it so concatenation with to.fullPath (leading slash) stays single-slash
        // and ReturnUrl resolves to /jobs/<path> under the mounted basepath.
        const base = (window.__APP_BASE__ || '/').replace(/\/+$/, '');
        window.location.href = '/Login?ReturnUrl=' + encodeURIComponent(base + to.fullPath);
        return false;
    }
});

// Direction-aware route transition tagging. Sets `to.meta.transition` to one
// of `page-fwd | page-back | page-sib`; layouts read it from the v-slot scope
// and pass it to <transition :name="...">. This is the canonical Vue Router
// pattern documented at https://router.vuejs.org/guide/advanced/transitions.html
// — no composable required.
//
// IMPORTANT: read `to.path`, NOT `to.fullPath`. fullPath includes query/hash;
// using it would tag every `/jobs?q=...` filter change as a navigation needing
// a slide.
router.afterEach((to, from) => {
    const isPopstate = Date.now() - popstateAt < 100;

    const toSeg0    = (to.path   || '').split('/').filter(Boolean)[0] ?? '';
    const fromSeg0  = (from.path || '').split('/').filter(Boolean)[0] ?? '';
    const toDepth   = (to.path   || '').split('/').filter(Boolean).length;
    const fromDepth = (from.path || '').split('/').filter(Boolean).length;

    let name: 'page-fwd' | 'page-back' | 'page-sib';
    if (isPopstate)               name = 'page-back';
    else if (toSeg0 !== fromSeg0) name = 'page-fwd';   // cross-layout switch
    else if (toDepth > fromDepth) name = 'page-fwd';
    else if (toDepth < fromDepth) name = 'page-back';
    else                          name = 'page-sib';

    to.meta.transition = name;
    isNavigating.value = false;
});

// A guard's window.location.href redirect (external MVC navigation) or a
// failed dynamic import (offline, stale deploy) both skip afterEach, and
// without this the bar would stay stuck on screen.
router.onError(() => { isNavigating.value = false; });

export default router;
